import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, Modal, Form, Row, Col } from 'react-bootstrap';
import { Plus, Pencil, Trash2, QrCode, Link45deg, People, Download, Printer, DoorOpen } from 'react-bootstrap-icons';
import { useAdminMenu } from '../../context/AdminMenuContext';
import { roomAPI, cleanupActionAPI } from '../../services/room';
import QRCodeLib from 'qrcode';

// QR Code Modal
const QRCodeModal = ({ show, onHide, table, branch }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (show && canvasRef.current && table && branch) {
      const baseUrl = process.env.REACT_APP_MENU_URL;
      const branchSlug = branch.code || branch.slug || branch.name.toLowerCase().replace(/\s+/g, '-');
      const tableSlug = table.slug || `ban-${table.number}`;
      const qrUrl = `${baseUrl}/menu/${branchSlug}/${tableSlug}`;

      QRCodeLib.toCanvas(
        canvasRef.current,
        qrUrl,
        {
          width: 300,
          margin: 2,
          color: {
            dark: '#6C4E31',
            light: '#FFFFFF',
          },
        },
        (error) => {
          if (error) console.error('Error generating QR code:', error);
        },
      );
    }
  }, [show, table, branch]);

  const handleDownload = () => {
    if (canvasRef.current) {
      const url = canvasRef.current.toDataURL('image/png');
      const link = document.createElement('a');
      const branchSlug = branch.code || branch.slug || branch.name.toLowerCase().replace(/\s+/g, '-');
      const tableSlug = table.slug || `ban-${table.number}`;
      link.download = `${branchSlug}-${tableSlug}-qr.png`;
      link.href = url;
      link.click();
    }
  };

  const handlePrint = () => {
    if (canvasRef.current) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const imageUrl = canvasRef.current.toDataURL('image/png');
        const baseUrl = process.env.REACT_APP_MENU_URL || window.location.origin;
        const branchSlug = branch.code || branch.slug || branch.name.toLowerCase().replace(/\s+/g, '-');
        const tableSlug = table.slug || `ban-${table.number}`;
        const menuUrl = `${baseUrl}/menu/${branchSlug}/${tableSlug}`;

        printWindow.document.write(`
          <html>
            <head>
              <title>QR Code - Bàn ${table.number}</title>
              <style>
                body {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  min-height: 100vh;
                  margin: 0;
                  font-family: system-ui, -apple-system, sans-serif;
                }
                .container {
                  text-align: center;
                  padding: 40px;
                }
                h1 {
                  color: #6C4E31;
                  margin-bottom: 10px;
                }
                .store-name {
                  color: #666;
                  font-size: 18px;
                  margin-bottom: 30px;
                }
                img {
                  max-width: 300px;
                  height: auto;
                }
                .instructions {
                  margin-top: 30px;
                  color: #666;
                  font-size: 14px;
                }
                .url {
                  margin-top: 20px;
                  color: #999;
                  font-size: 12px;
                  font-family: monospace;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <h1>Bàn số ${table.number}</h1>
                <div class="store-name">${branch.name}</div>
                <img src="${imageUrl}" alt="QR Code" />
                <div class="instructions">
                  Quét mã QR để xem thực đơn và đặt món
                </div>
                <div class="url">${menuUrl}</div>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  if (!table || !branch) return null;

  const branchSlug = branch.code || branch.slug || branch.name.toLowerCase().replace(/\s+/g, '-');
  const tableSlug = table.slug || `ban-${table.number}`;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>QR Code - Bàn {table.number}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center">
          <p className="text-muted mb-3">{branch.name}</p>
          <div className="d-flex justify-content-center bg-white p-3 rounded">
            <canvas ref={canvasRef} />
          </div>
          <p className="text-muted mt-3 small">Quét mã QR để xem thực đơn và đặt món</p>
          <code className="bg-light px-2 py-1 rounded d-inline-block small">
            /menu/{branchSlug}/{tableSlug}
          </code>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-primary"
          onClick={handleDownload}
        >
          <Download
            size={16}
            className="me-1"
          />
          Tải xuống
        </Button>
        <Button
          variant="outline-primary"
          onClick={handlePrint}
        >
          <Printer
            size={16}
            className="me-1"
          />
          In QR Code
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Table Form Modal
const TableModal = ({ show, onHide, table, branches, onSave }) => {
  const [formData, setFormData] = useState({
    branchId: '',
    number: '',
    slug: '',
    capacity: 2,
    isAvailable: true,
    isActive: true,
    roomId: '',
    cleanupActionsOverride: [],
  });
  // Phòng + catalog hành động dọn của chi nhánh đang chọn
  const [rooms, setRooms] = useState([]);
  const [actions, setActions] = useState([]);

  useEffect(() => {
    if (table) {
      setFormData({
        branchId: table.branchId || table.storeId,
        number: typeof table.number === 'number' ? table.number : (table.number != null ? Number(table.number) : ''),
        slug: table.slug || '',
        capacity: table.capacity,
        isAvailable: table.isAvailable,
        isActive: table.isActive,
        roomId: table.roomId || '',
        cleanupActionsOverride: table.cleanupActionsOverride || [],
      });
    } else {
      setFormData({
        branchId: branches[0]?.id || '',
        number: '',
        slug: '',
        capacity: 2,
        isAvailable: true,
        isActive: true,
        roomId: '',
        cleanupActionsOverride: [],
      });
    }
  }, [table, branches, show]);

  // Load rooms + cleanup actions theo chi nhánh đang chọn
  useEffect(() => {
    const branchId = formData.branchId;
    if (!show || !branchId) {
      setRooms([]);
      setActions([]);
      return;
    }
    let active = true;
    Promise.all([
      roomAPI.getRooms({ branchId }).catch(() => ({ data: [] })),
      cleanupActionAPI.getActions({ branchId }).catch(() => ({ data: [] })),
    ]).then(([roomRes, actionRes]) => {
      if (!active) return;
      setRooms(roomRes?.data || []);
      setActions(actionRes?.data || []);
    });
    return () => {
      active = false;
    };
  }, [show, formData.branchId]);

  const toggleOverride = (code) => {
    setFormData((prev) => {
      const has = prev.cleanupActionsOverride.includes(code);
      return {
        ...prev,
        cleanupActionsOverride: has
          ? prev.cleanupActionsOverride.filter((c) => c !== code)
          : [...prev.cleanupActionsOverride, code],
      };
    });
  };

  const selectedRoom = rooms.find((r) => r.id === formData.roomId);
  const inheritedCodes = selectedRoom?.default_cleanup_actions || [];
  const actionLabel = (code) =>
    actions.find((a) => a.code === code)?.label || code;

  const handleNumberChange = (value) => {
    if (value === '' || value === undefined) {
      setFormData({ ...formData, number: '', slug: '' });
      return;
    }
    const num = parseInt(value, 10);
    const safeNum = Number.isNaN(num) || num < 1 ? 1 : num;
    setFormData({ ...formData, number: safeNum, slug: `ban-${safeNum}` });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...formData };
    if (typeof payload.number !== 'number') payload.number = parseInt(formData.number, 10) || 1;
    onSave(payload);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{table ? 'Chỉnh sửa bàn' : 'Thêm bàn mới'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Chi nhánh *</Form.Label>
            <Form.Select
              value={formData.branchId}
              onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
              required
            >
              <option value="">Chọn chi nhánh</option>
              {branches.map((branch) => (
                <option
                  key={branch.id}
                  value={branch.id}
                >
                  {branch.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Số bàn *</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={formData.number === '' ? '' : formData.number}
                  onChange={(e) => handleNumberChange(e.target.value)}
                  placeholder="VD: 1, 2, 3"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Số chỗ ngồi *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="20"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: Number(e.target.value) })}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-2">
            <Form.Check
              type="switch"
              id="table-available"
              label="Bàn trống"
              checked={formData.isAvailable}
              onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="table-active"
              label="Hoạt động"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
          </Form.Group>

          <hr />

          <Form.Group className="mb-3">
            <Form.Label className="d-flex align-items-center gap-1">
              <DoorOpen size={15} /> Phòng
            </Form.Label>
            <Form.Select
              value={formData.roomId}
              onChange={(e) =>
                setFormData({ ...formData, roomId: e.target.value })
              }
            >
              <option value="">Khu chung (không thuộc phòng)</option>
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Bàn thuộc phòng sẽ kế thừa hành động dọn mặc định của phòng.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>Override hành động dọn (theo bàn)</Form.Label>
            {actions.length === 0 ? (
              <div className="text-muted small">
                Chi nhánh chưa có hành động dọn nào. Thêm ở tab "Phòng".
              </div>
            ) : (
              <>
                <div className="d-flex flex-wrap gap-3">
                  {actions.map((a) => (
                    <Form.Check
                      key={a.id}
                      type="checkbox"
                      id={`override-${a.id}`}
                      label={a.label}
                      checked={formData.cleanupActionsOverride.includes(a.code)}
                      onChange={() => toggleOverride(a.code)}
                    />
                  ))}
                </div>
                <Form.Text className="text-muted">
                  {formData.cleanupActionsOverride.length === 0
                    ? inheritedCodes.length > 0
                      ? `Trống = kế thừa từ phòng: ${inheritedCodes.map(actionLabel).join(', ')}`
                      : 'Trống = không có hành động đi kèm (hoặc kế thừa phòng nếu có).'
                    : 'Đã override — chỉ dùng các hành động được chọn, bỏ qua mặc định của phòng.'}
                </Form.Text>
              </>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            type="submit"
          >
            {table ? 'Cập nhật' : 'Thêm'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const AdminTables = () => {
  const { tables, branches, fetchTables, createTable, updateTable, deleteTable } = useAdminMenu();
  const [showModal, setShowModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [qrData, setQrData] = useState(null);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const handleEdit = (table) => {
    setEditingTable(table);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingTable(null);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingTable) {
        await updateTable(editingTable.id, formData);
      } else {
        await createTable(formData);
      }
      setShowModal(false);
      setEditingTable(null);
    } catch (error) {
      console.error('Error saving table:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bạn có chắc muốn xóa bàn này?')) {
      try {
        await deleteTable(id);
      } catch (error) {
        console.error('Error deleting table:', error);
      }
    }
  };

  const handleShowQR = (table) => {
    const branch = branches.find((b) => b.id === (table.branchId || table.storeId));
    if (branch) {
      setQrData({ table, branch });
      setShowQRModal(true);
    }
  };

  const copyTableUrl = (table) => {
    const branch = branches.find((b) => b.id === (table.branchId || table.storeId));
    if (branch) {
      const baseUrl = process.env.REACT_APP_MENU_URL || window.location.origin;
      const branchSlug = branch.code || branch.slug || branch.name.toLowerCase().replace(/\s+/g, '-');
      const tableSlug = table.slug || `ban-${table.number}`;
      const url = `${baseUrl}/menu/${branchSlug}/${tableSlug}`;
      navigator.clipboard.writeText(url);
      alert('Đã sao chép đường dẫn!');
    }
  };

  // Group tables by branch
  const tablesByBranch = branches.reduce((acc, branch) => {
    acc[branch.id] = tables.filter((t) => (t.branchId || t.storeId) === branch.id);
    return acc;
  }, {});

  return (
    <>
      <div className="mb-4">
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4">
          <div>
            <h4 className="mb-1">Quản lý bàn</h4>
            <small className="text-muted">Quản lý bàn cho từng chi nhánh</small>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAdd}
          >
            <Plus
              size={18}
              className="me-1"
            />
            Thêm bàn
          </Button>
        </div>

        <div className="d-flex flex-column gap-4">
          {branches.map((branch) => {
            const branchTables = tablesByBranch[branch.id] || [];

            return (
              <Card
                key={branch.id}
                className="shadow-sm"
              >
                <Card.Header className="bg-light">
                  <h5 className="mb-0">{branch.name}</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    {branchTables.map((table) => {
                      const branchSlug = branch.code || branch.slug || branch.name.toLowerCase().replace(/\s+/g, '-');
                      const tableSlug = table.slug || `ban-${table.number}`;

                      return (
                        <Col
                          key={table.id}
                          xs={12}
                          sm={6}
                          lg={4}
                        >
                          <Card className="h-100 border">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="display-6 fw-bold text-primary">#{table.number}</div>
                                  <Badge bg={table.isAvailable ? 'success' : 'secondary'}>
                                    {table.isAvailable ? 'Trống' : 'Đang dùng'}
                                  </Badge>
                                </div>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleShowQR(table)}
                                    title="Xem QR Code"
                                  >
                                    <QrCode size={16} />
                                  </Button>
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => copyTableUrl(table)}
                                    title="Sao chép link"
                                  >
                                    <Link45deg size={16} />
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleEdit(table)}
                                  >
                                    <Pencil size={16} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDelete(table.id)}
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2 text-muted mb-3">
                                <People size={16} />
                                <span className="small">{table.capacity} người</span>
                              </div>
                              <div className="border-top pt-2">
                                <p className="text-muted mb-1 small">Đường dẫn thực đơn:</p>
                                <code className="bg-light px-2 py-1 rounded d-block text-truncate small">
                                  /menu/{branchSlug}/{tableSlug}
                                </code>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                  {branchTables.length === 0 && (
                    <div className="text-center py-5 text-muted">Chưa có bàn nào cho chi nhánh này</div>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </div>

        {branches.length === 0 && (
          <Card className="shadow-sm">
            <Card.Body className="text-center py-5 text-muted">
              Chưa có chi nhánh nào. Vui lòng thêm chi nhánh trước!
            </Card.Body>
          </Card>
        )}
      </div>

      <TableModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingTable(null);
        }}
        table={editingTable}
        branches={branches}
        onSave={handleSave}
      />

      {qrData && (
        <QRCodeModal
          show={showQRModal}
          onHide={() => {
            setShowQRModal(false);
            setQrData(null);
          }}
          table={qrData.table}
          branch={qrData.branch}
        />
      )}
    </>
  );
};

export default AdminTables;
